import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useAllPOs } from '@/hooks/usePOs'
import { fmtDate } from '@/lib/dates'
import { useUnarchivePO } from '@/hooks/usePOMutations'

/**
 * Recover an accidentally-archived PO by flipping it back to ORDERED and
 * restoring Shopify INCOMING for the unreceived portion. AVAILABLE is left
 * intact — units already received stay on hand. See backend
 * routers/purchase_orders.unarchive_po for the math.
 */
export function UnarchivePOSection() {
  const { data: pos = [], isLoading } = useAllPOs()
  const unarchiveMut = useUnarchivePO()
  const [pendingId, setPendingId] = useState<string | null>(null)

  const archived = useMemo(() => {
    // Most-recently archived first — those are the most likely fat-finger
    // recoveries an admin is looking for.
    return pos
      .filter((p) => p.status === 'ARCHIVED')
      .slice()
      .sort((a, b) => {
        const ax = a.updated_at ?? ''
        const bx = b.updated_at ?? ''
        return bx.localeCompare(ax)
      })
  }, [pos])

  function handleUnarchive(po_id: string, label: string) {
    setPendingId(po_id)
    toast(`Unarchive PO ${label}? Shopify INCOMING will be restored for the unreceived portion; AVAILABLE is left alone.`, {
      action: {
        label: 'Unarchive',
        onClick: () => {
          unarchiveMut.mutate(po_id, {
            onSuccess: () => {
              toast.success(`Unarchived ${label} → ORDERED`)
              setPendingId(null)
            },
            onError: (err) => {
              toast.error(err instanceof Error ? err.message : 'Unarchive failed')
              setPendingId(null)
            },
          })
        },
      },
      onDismiss: () => setPendingId(null),
      onAutoClose: () => setPendingId(null),
    })
  }

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold">Unarchive PO</h2>
        <p className="text-xs text-[var(--mute)]">
          Restore an archived PO back to ORDERED — e.g. when a buyer
          accidentally archived an active order. Shopify INCOMING for the
          unreceived portion is re-added; units already received stay on
          hand. Use the receiving page&apos;s − button to unreceive any unit.
        </p>
      </div>

      {isLoading ? (
        <p className="text-xs text-[var(--mute)]">Loading archived POs…</p>
      ) : archived.length === 0 ? (
        <p className="text-xs text-[var(--mute)]">No archived POs.</p>
      ) : (
        <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] rounded overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-[var(--surface-header)] text-[var(--ink-2)]">
              <tr>
                <th className="tbl-th text-left">PO</th>
                <th className="tbl-th text-left">Vendor</th>
                <th className="tbl-th text-right">Items</th>
                <th className="tbl-th text-left">Updated</th>
                <th className="tbl-th"></th>
              </tr>
            </thead>
            <tbody>
              {archived.map((p, i) => {
                const label = p.po_number || p.po_id
                const updated = fmtDate(p.updated_at)
                const busy = unarchiveMut.isPending && pendingId === p.po_id
                return (
                  <tr
                    key={p.po_id}
                    className={`border-t border-[var(--line-warm)] ${
                      i % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'
                    }`}
                  >
                    <td className="p-2 font-mono">{label}</td>
                    <td className="p-2">{p.vendor || '—'}</td>
                    <td className="p-2 text-right tabular-nums">{p.items_count}</td>
                    <td className="p-2 whitespace-nowrap text-[var(--mute)]">{updated || '—'}</td>
                    <td className="p-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-xs"
                        onClick={() => handleUnarchive(p.po_id, label)}
                        disabled={busy}
                      >
                        {busy ? 'Unarchiving…' : 'Unarchive'}
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
